import { _isEmpty, _isNil, _notNil } from '@/littledash';
import type { AnimalApiId } from '@/model/Animal.model';
import type { ISODateTime, Nullable } from '@/model/Common.model';
import type { FC } from 'react';
import { useDispatch } from 'react-redux';

type LatestObservation = { title: string; value: string | number | null };

interface LatestObservationsCellProps {
  animalApiId: Nullable<AnimalApiId>;
  animalName: Nullable<string>;
  latestObservations: Nullable<Array<LatestObservation>>;
  latestObservedAt: Nullable<ISODateTime>;
}

const formatObservation = (obs: LatestObservation) => (_notNil(obs.value) ? `${obs.title} (${obs.value})` : obs.title);

const LatestObservationsCell: FC<LatestObservationsCellProps> = ({
  animalApiId,
  animalName,
  latestObservations,
  latestObservedAt,
}) => {
  const dispatch = useDispatch();

  let displayText: string;

  if (_isEmpty(latestObservations) || _isNil(latestObservations)) {
    displayText = '-';
  } else if (latestObservations.length === 1) {
    displayText = formatObservation(latestObservations[0]);
  } else if (latestObservations.length === 2) {
    displayText = `${formatObservation(latestObservations[0])}, ${formatObservation(latestObservations[1])}`;
  } else {
    displayText = `${formatObservation(latestObservations[0])}, ${formatObservation(latestObservations[1])} +more`;
  }

  const handleClick = () => {
    if (_isNil(animalApiId) || _isEmpty(latestObservations) || _isNil(latestObservations)) {
      return;
    }

    dispatch({
      type: 'OPEN_MODAL',
      modal: 'LATEST_OBSERVATIONS',
      props: {
        animalApiId,
        animalName: animalName ?? 'Animal',
        latestObservedAt,
      },
    });
  };

  const hasObservations = _notNil(latestObservations) && latestObservations.length > 0;

  return (
    <div
      className={hasObservations ? 'link blue pointer' : ''}
      onClick={hasObservations ? handleClick : undefined}
      data-test-component="LatestObservationsCell"
      data-test-element="container"
    >
      {displayText}
    </div>
  );
};

export default LatestObservationsCell;
